import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { Plus, Search } from 'lucide-react';
import { useState } from 'react';

import { RevenueAccountFormModal } from '@apps/web/components/admin/revenue/revenue_account_form_modal';
import {
    formatCurrencyFromCents,
    formatDate,
    REVENUE_ACCOUNT_STATUS_OPTIONS,
} from '@apps/web/components/admin/revenue/revenue_utils';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Input } from '@apps/web/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { getNavigableElementProps } from '@apps/web/lib/navigation_utils';
import {
    useAdminRevenueAccounts,
    useAdminRevenueCustomerAccounts,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type RevenueAccountStatus } from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/admin/revenue/accounts')({
    head: () => ({
        meta: [
            {
                title: 'Revenue Accounts | Phoebe Admin',
            },
        ],
    }),
    component: AdminRevenueAccountsPage,
});

function AdminRevenueAccountsPage() {
    const router = useRouter();
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<
        RevenueAccountStatus | 'all'
    >('all');
    const [customerAccountFilter, setCustomerAccountFilter] = useState<
        string | null
    >(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const { data: accountsResponse, isLoading } = useAdminRevenueAccounts({
        page: 1,
        per_page: 100,
        ...(searchQuery ? { search: searchQuery } : {}),
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        ...(customerAccountFilter
            ? { customer_account_id: customerAccountFilter }
            : {}),
    });
    const {
        data: customerAccountsResponse,
        isLoading: areCustomerAccountsLoading,
    } = useAdminRevenueCustomerAccounts();

    const accounts = accountsResponse?.accounts ?? [];
    const customerAccounts = customerAccountsResponse?.customer_accounts ?? [];
    const selectedCustomerAccount = customerAccounts.find(
        (customerAccount) =>
            customerAccount.customer_account_id === customerAccountFilter,
    );
    const getAccountNavigation = (accountId: string) => {
        const routeOptions = {
            to: '/admin/revenue/$account-id',
            params: { 'account-id': accountId },
        } as const;
        return {
            href: router.buildLocation(routeOptions).href,
            navigate: () => {
                void router.navigate(routeOptions);
            },
        };
    };

    return (
        <div className="flex h-full min-w-0 flex-col overflow-hidden">
            <div className="border-border min-w-0 border-b px-4 py-4 sm:px-6">
                <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div className="min-w-0">
                        <div className="mb-1 flex items-center gap-2">
                            <Link
                                to="/admin/revenue"
                                className="text-muted-foreground hover:text-foreground text-sm transition-colors"
                            >
                                Revenue
                            </Link>
                            <span className="text-muted-foreground text-sm">
                                /
                            </span>
                            <span className="text-sm font-medium">
                                Accounts
                            </span>
                        </div>
                        <h2 className="text-lg font-semibold">
                            Revenue Accounts
                        </h2>
                        <p className="text-muted-foreground text-sm">
                            {accountsResponse?.total ?? 0} tracked accounts
                        </p>
                    </div>
                    <Button
                        onClick={() => setIsCreateModalOpen(true)}
                        className="w-full gap-2 sm:w-auto"
                    >
                        <Plus className="h-4 w-4" />
                        Create Account
                    </Button>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative max-w-sm min-w-[260px] flex-1">
                        <Search className="text-muted-foreground absolute top-2.5 left-3 h-4 w-4" />
                        <Input
                            placeholder="Search organizations or external references…"
                            className="pl-9"
                            value={searchQuery}
                            onChange={(event) =>
                                setSearchQuery(event.target.value)
                            }
                        />
                    </div>
                    <Select
                        value={statusFilter}
                        onValueChange={(value) =>
                            setStatusFilter(
                                value as RevenueAccountStatus | 'all',
                            )
                        }
                    >
                        <SelectTrigger className="w-full sm:w-[180px]">
                            <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {REVENUE_ACCOUNT_STATUS_OPTIONS.map((option) => (
                                <SelectItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className="min-w-0 flex-1 overflow-y-auto">
                <div className="mx-auto w-full max-w-7xl space-y-8 p-4 sm:p-6">
                    <section className="space-y-3">
                        <div>
                            <h3 className="font-semibold">Customer Accounts</h3>
                            <p className="text-muted-foreground text-sm">
                                Select a customer to inspect its organization
                                revenue lines.
                            </p>
                        </div>
                        {areCustomerAccountsLoading ? (
                            <div className="text-muted-foreground flex h-24 items-center justify-center gap-2 text-sm">
                                <Spinner className="h-4 w-4" />
                                <span>Loading customer accounts…</span>
                            </div>
                        ) : customerAccounts.length === 0 ? (
                            <div className="text-muted-foreground flex h-24 items-center justify-center text-sm">
                                No customer revenue accounts yet
                            </div>
                        ) : (
                            <div className="overflow-x-auto rounded-md border">
                                <Table className="min-w-[680px]">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Customer</TableHead>
                                            <TableHead>Organizations</TableHead>
                                            <TableHead>Revenue Lines</TableHead>
                                            <TableHead>
                                                Contracted ARR
                                            </TableHead>
                                            <TableHead>Pending ARR</TableHead>
                                            <TableHead>Live ARR</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {customerAccounts.map(
                                            (customerAccount) => (
                                                <TableRow
                                                    key={
                                                        customerAccount.customer_account_id
                                                    }
                                                    data-testid="customer-account-rollup-row"
                                                >
                                                    <TableCell>
                                                        <Button
                                                            variant="link"
                                                            className="h-auto p-0 font-medium"
                                                            onClick={() =>
                                                                setCustomerAccountFilter(
                                                                    customerAccount.customer_account_id,
                                                                )
                                                            }
                                                        >
                                                            {
                                                                customerAccount.customer_account_name
                                                            }
                                                        </Button>
                                                    </TableCell>
                                                    <TableCell>
                                                        {
                                                            customerAccount.organization_count
                                                        }
                                                    </TableCell>
                                                    <TableCell>
                                                        {
                                                            customerAccount.revenue_account_count
                                                        }
                                                    </TableCell>
                                                    <TableCell>
                                                        {formatCurrencyFromCents(
                                                            customerAccount.contracted_arr_cents,
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {formatCurrencyFromCents(
                                                            customerAccount.pending_arr_cents,
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {formatCurrencyFromCents(
                                                            customerAccount.live_arr_cents,
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ),
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </section>
                    <section
                        className="space-y-3"
                        data-testid="organization-revenue-lines"
                    >
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <h3 className="font-semibold">
                                    Organization Revenue Lines
                                </h3>
                                <p className="text-muted-foreground text-sm">
                                    {accountsResponse?.total ?? 0} lines
                                    {selectedCustomerAccount
                                        ? ` for ${selectedCustomerAccount.customer_account_name}`
                                        : ''}
                                </p>
                            </div>
                            {customerAccountFilter ? (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        setCustomerAccountFilter(null)
                                    }
                                >
                                    Show all customers
                                </Button>
                            ) : null}
                        </div>
                        {isLoading ? (
                            <div className="text-muted-foreground flex h-32 items-center justify-center gap-2 text-sm">
                                <Spinner className="h-4 w-4" />
                                <span>Loading revenue accounts…</span>
                            </div>
                        ) : accounts.length === 0 ? (
                            <div className="text-muted-foreground flex h-24 items-center justify-center text-sm">
                                No matching revenue lines
                            </div>
                        ) : (
                            <div className="overflow-x-auto rounded-md border">
                                <Table className="min-w-[820px]">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Organization</TableHead>
                                            <TableHead>Customer</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>
                                                Contracted ARR
                                            </TableHead>
                                            <TableHead>Pending ARR</TableHead>
                                            <TableHead>Live ARR</TableHead>
                                            <TableHead>Close</TableHead>
                                            <TableHead>Go Live</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {accounts.map((account) => (
                                            <TableRow
                                                key={account.id}
                                                className="cursor-pointer"
                                                {...getNavigableElementProps(
                                                    getAccountNavigation(
                                                        account.id,
                                                    ),
                                                )}
                                            >
                                                <TableCell>
                                                    <div className="font-medium">
                                                        {account.name}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    {
                                                        account.customer_account_name
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline">
                                                        {
                                                            REVENUE_ACCOUNT_STATUS_OPTIONS.find(
                                                                (option) =>
                                                                    option.value ===
                                                                    account.status,
                                                            )?.label
                                                        }
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    {formatCurrencyFromCents(
                                                        account.contracted_arr_cents,
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {formatCurrencyFromCents(
                                                        account.pending_arr_cents,
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {formatCurrencyFromCents(
                                                        account.live_arr_cents,
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {formatDate(
                                                        account.close_date,
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {formatDate(
                                                        account.go_live_date,
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </section>
                </div>
            </div>
            <RevenueAccountFormModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                mode="create"
                onSaveSuccess={(account) => {
                    setIsCreateModalOpen(false);
                    router.navigate({
                        to: '/admin/revenue/$account-id',
                        params: { 'account-id': account.id },
                    });
                }}
            />
        </div>
    );
}
